import rest from 'redux/rest'
import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { fbq, constants, links } from 'common/helpers'
import { getFeatureToggle, getCookieValue } from 'helpers'
import { modifyCart } from 'common/helpers/modifiers/modifyResponse'


const modifyCartDataResponse = ({ recommendationTag, ...rest } = {}) => {
  if (recommendationTag) {
    actions.models.recommendations.setTag(recommendationTag)
  }

  return modifyCart(rest)
}


const open = () => reducers.models.cart.setVisibility(true)

const close = () => {
  reducers.models.cart.setVisibility(false)
  // TODO looks weird - why do we try to open modal if we just close cart overlay, we try open it on changes in updateCartState()
  tryOpenRecommendationTagModal()
}

const tryOpenRecommendationTagModal = () => {
  const { models: { recommendations: { tag }, cart: { isVisible } } } = getState()

  if (tag && !isVisible) {
    actions.models.recommendations.fetch({ placement: 'cart' })
  }
}

const updateCartState = (result) => {
  // TODO looks weird - we call this before state update
  tryOpenRecommendationTagModal()
  reducers.models.cart.update(result)
}

const fetch = () =>
  rest.models.cart.fetch({
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { isFlashSaleRemoved, isFlashSaleExists } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExists })
      updateCartState(result)

      return result
    })

const fetchTotalPrice = () =>
  rest.models.cart.fetchTotalPrice()
    .then(({ price }) => {
      reducers.models.cart.setTotalPrice(price)
      return price
    })

const _addItem = (item, metadata = {}, count = 1, withNotification = true) => {
  const { id, tradingId, price } = item
  const { placement } = metadata

  fbq.track('AddToCart', {
    content_type: 'product',
    content_ids: [ id ],
    content_name: 'Shopping Cart',
    value: price,
    currency: 'USD',
  })

  reducers.models.cart.setFetching(true)

  return rest.models.cart.postCart({
    body: {
      item: tradingId || id,
      count,
      metadata,
    },
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { models: { cart: { isVisible, items: { beautyBag: beautyBagOld } } } } = getState()

      if (!isVisible && placement !== 'toss-in page') {
        const { items: { beautyBag } } = result

        const isBeautyBagEnabled = getFeatureToggle(constants.features.beautyBag)

        if (isBeautyBagEnabled && beautyBag) {
          const { stepsLeft: stepsLeftOld } = beautyBagOld
          const { stepsLeft, items } = beautyBag

          if (stepsLeft !== stepsLeftOld) {
            actions.misc.notifications.show('beautyBag', {
              image: items[0].image,
              stepsLeft,
              onClose: () => open(),
            })
          }
        }

        if (withNotification) {
          actions.misc.notifications.show('perfumeItem', {
            item,
            onClose: () => open(),
            placement,
          })
        }
      }

      const { isFlashSaleRemoved, isFlashSaleExists } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExists })
      updateCartState(result)

      return result
    })
    .finally(() => reducers.models.cart.setFetching(false))
}

// Save and add product after registration / authorization
const _addItemPostponed = (item, metadata, withNotification) => {
  const cartProduct = { item: { ...item, amount: 1 }, metadata, withNotification }

  actions.misc.localStorage.setItem(constants.localStorageNames.cartProduct, cartProduct)
  // window.location.href for reloading page if redirect from SEO page
  actions.models.router.push(links.register, true, true)

  return Promise.resolve()
}

const moveCartProductFromCookieToStorage = () => {
  const cartProduct = getCookieValue(constants.cookieNames.postponed.cartProduct)

  if (cartProduct) {
    actions.misc.localStorage.setItem(constants.localStorageNames.cartProduct, cartProduct)
    actions.misc.cookies.removeItem(constants.cookieNames.postponed.cartProduct)
  }
}

const resolvePostponedItem = () => {
  const cartProduct = actions.misc.localStorage.getItem(constants.localStorageNames.cartProduct, {})

  const { item, metadata, withNotification } = cartProduct

  actions.misc.localStorage.removeItem(constants.localStorageNames.cartProduct)

  if (item) {
    return _addItem(item, metadata, item.amount || 1, withNotification)
  }

  return Promise.resolve()
}

/**
 * Add product to the cart
 *
 * @param {Object} item
 * @param {Number} item.id
 * @param {Number} item.price
 * @param {Number} [item.amount]
 * @param {Number} [item.tradingId]
 * @param {Object} metadata
 * @param {String} metadata.placement
 * @param {Boolean} [withNotification]
 * @returns {*}
 */
const addItem = (item, metadata, withNotification = true) => {
  const { models: { me: { isLoggedIn } } } = getState()

  if (isLoggedIn) {
    const amount = item.amount || 1

    return _addItem(item, metadata, amount, withNotification)
  }

  return _addItemPostponed(item, metadata, withNotification)
}

const updateItem = (id, { totalCount, totalPrice, items }) => {
  updateCartState({ totalCount, totalPrice, isFetching: false })

  const product = items.products.items[id]
  reducers.models.cart.updateProduct({ id, product })
}

// we delete products one by one, this is bad when user wants to click + or - button many times
// frontend should send to the server final amount of products which must be set
// it requires rework of the query to the server, can be done while moving from REST to GQL
const updateItemCount = (id, count) => {
  reducers.models.cart.updateProductAmount({ id, amount: count })
}

const increaseItemCount = ({ id, amount }) => {
  updateCartState({ isFetching: true })

  return request.post(`rest/cart/items/${id}`, {
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { isFlashSaleRemoved, isFlashSaleExists } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExists })
      updateItem(id, result)

      return result
    })
}


const decreaseItemCount = ({ id, amount }) => {
  updateCartState({ isFetching: true })

  return request.delete(`rest/cart/items/${id}`, {
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { isFlashSaleRemoved, isFlashSaleExists } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExists })
      updateItem(id, result)

      return result
    })
}

const removeProduct = ({ id }) => {
  reducers.models.cart.setFetching(true)

  return request.delete(`rest/cart/items/${id}?all=true`, {
    modifyResult: modifyCartDataResponse,
  })
    .then((result) => {
      const { isFlashSaleRemoved, isFlashSaleExists } = result

      actions.view.flashSaleBundlePage.handleCartUpdate({ isFlashSaleRemoved, isFlashSaleExists })
      updateItem(id, result)

      return result
    })
    .finally(() => reducers.models.cart.setFetching(false))
}

const addSubscription = (withLoader = true) => {
  if (withLoader) {
    actions.models.ui.showRequestLoader()
  }

  return request.put('rest/cart/subscription')
    .finally(() => withLoader ? actions.models.ui.hideRequestLoader() : null)
}

const removeSubscription = () =>
  request.delete(`rest/cart/subscription`)
    .then((result) => {
      fetch()

      return result
    })

const clear = () => {
  reducers.models.cart.clear()
}


export default {
  open,
  close,
  fetch,
  fetchTotalPrice,
  addItem,
  resolvePostponedItem,
  increaseItemCount,
  decreaseItemCount,
  removeProduct,
  addSubscription,
  removeSubscription,
  clear,
  moveCartProductFromCookieToStorage,
}
